import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import JarvisChat from './pages/JarvisChat';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Sessions from './pages/Sessions';
import SessionDetail from './pages/SessionDetail';
import BlueprintView from './pages/BlueprintView';
import Profile from './pages/Profile';
import ChangePassword from './pages/ChangePassword';
import UserManagement from './pages/UserManagement';
import ContactManagement from './pages/ContactManagement';
import Blueprints from './pages/Blueprints';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// ローディング画面
function LoadingScreen() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-jarvis-navy-dark via-jarvis-navy to-jarvis-navy-dark flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-jarvis-gold flex items-center justify-center animate-pulse">
          <span className="text-jarvis-navy-dark font-bold text-2xl">J</span>
        </div>
        <p className="text-gray-400">読み込み中...</p>
      </div>
    </div>
  );
}

// 認証が必要なルート
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// 管理者のみアクセス可能
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* パブリックルート */}
      <Route path="/" element={<JarvisChat />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      
      {/* 認証が必要なルート */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sessions"
        element={
          <ProtectedRoute>
            <Sessions />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sessions/:id"
        element={
          <ProtectedRoute>
            <SessionDetail />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/sessions/:id/blueprint"
        element={
          <ProtectedRoute>
            <BlueprintView />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        }
      />

      {/* 管理者ルート */}
      <Route
        path="/admin/users"
        element={
          <AdminRoute>
            <UserManagement />
          </AdminRoute>
        }
      />

      <Route
        path="/admin/contacts"
        element={
          <AdminRoute>
            <ContactManagement />
          </AdminRoute>
        }
      />

      <Route
        path="/blueprints"
        element={
          <ProtectedRoute>
            <Blueprints />
          </ProtectedRoute>
        }
      />
      
      {/* 404 */}
      <Route
        path="*"
        element={
          <div className="min-h-screen bg-gradient-to-br from-jarvis-navy-dark via-jarvis-navy to-jarvis-navy-dark flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-jarvis-gold text-6xl font-bold mb-4">404</h1>
              <p className="text-gray-400 mb-8">お探しのページが見つかりません</p>
              <a href="/" className="btn-primary">
                ホームに戻る
              </a>
            </div>
          </div>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
